import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram } from "lucide-react";
import Reveal from "@/components/Reveal";

const founderPhoto = "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1600&q=85";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-16 md:pb-24 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>About Simply Social</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-[0.9] max-w-[14ch]">
          One decision.<br/>One night at a time.
        </h1>
        <p className="mt-8 font-body text-lg md:text-2xl max-w-3xl leading-relaxed" style={{ color: "var(--ss-cream-dim)" }}>
          Simply Social started with one decision: <span style={{ color: "var(--ss-cream)" }}>I was ready to bet on myself.</span>
        </p>
      </section>

      {/* STORY */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32 grid md:grid-cols-12 gap-10 items-start" data-testid="about-story">
        <div className="md:col-span-5 md:sticky md:top-32">
          <div className="relative aspect-[4/5] overflow-hidden grain" style={{ background: "var(--ss-bg-2)" }}>
            <img src={founderPhoto} alt="Simply Social — behind the bar" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.05), rgba(10,10,11,0.55))" }} />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-label text-xs mb-2" style={{ color: "var(--ss-gold)" }}>From the Founder</div>
              <div className="font-display text-3xl md:text-4xl leading-none">Chicago · St. Louis</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 space-y-8">
          <Reveal>
            <p className="font-display font-light text-2xl md:text-4xl leading-[1.25] tracking-tight" style={{ color: "var(--ss-cream)" }}>
              I had reached a point where I wasn't happy with my job, and I knew I wanted something more. Instead of continuing to wait for the "right time" or trying to figure out every detail first, I decided to just go for it.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              The funny thing is, the foundation for Simply Social had been there long before I realized it.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              I grew up around parties and celebrations, so bringing people together has always been a part of my life. As I got older, I became the one throwing the parties, planning the details, creating the vibe and making sure people had a good time. I had also always wanted to bartend — not just because of the drinks, but because I loved the social side of it. The energy. The conversations. The experience.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              At the same time, my husband had his own love for music as a producer. One day, it just clicked. Why not take what we both genuinely enjoy and build something of our own?
            </p>
          </Reveal>

          <Reveal delay={200}>
            <div className="border-l-2 pl-6 md:pl-8 py-2" style={{ borderColor: "var(--ss-rust)" }}>
              <p className="font-display font-light text-3xl md:text-5xl leading-[1.15] tracking-tight">
                And that's how Simply Social was created.
              </p>
            </div>
          </Reveal>

          <Reveal delay={240}>
            <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              What started as us putting our interests together has grown into something much bigger. From mobile bartending and DJ services to event experiences, rentals and custom details, Simply Social is about creating the kind of atmosphere people remember after the event is over.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              This business is still growing, and so are we. But at the heart of it, the vision has stayed simple:
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="grid gap-3 py-2">
              <div className="flex items-baseline gap-4">
                <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>01</span>
                <span className="font-display text-2xl md:text-3xl tracking-tight">Bring people together.</span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>02</span>
                <span className="font-display text-2xl md:text-3xl tracking-tight">Create the vibe.</span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>03</span>
                <span className="font-display text-2xl md:text-3xl tracking-tight">Make it an experience.</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={360}>
            <p className="font-body text-lg leading-[1.75] italic" style={{ color: "var(--ss-cream-dim)" }}>
              Because we've always believed the best memories are made when people get together and simply… socialize.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-32">
        <div className="p-10 md:p-20 border flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
             style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
          <div>
            <div className="font-label text-xs mb-3" style={{ color: "var(--ss-gold)" }}>Follow along</div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tight">See what we've been building.</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href="https://instagram.com/simplysocialchicago" target="_blank" rel="noreferrer" data-testid="about-ig-btn" className="btn-outline">
              <Instagram size={14} /> @simplysocialchicago
            </a>
            <Link to="/book" data-testid="about-book-btn" className="btn-primary">Book Your Event <ArrowUpRight className="arrow" size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
